import { useEffect, useMemo, useRef, useState } from 'react';
import { initBirdie } from '@birdie-so/snippet';
import { VITE_BIRDIE_API_KEY } from 'src/constants/envs';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { BirdieContext } from './Birdie.context';
import { tBirdieProviderProps } from './Birdie.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import { useSubscriptionContext } from '../SubscriptionProvider/Subscription.context';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context';
import { getPlanName } from 'src/hooks/Intercom/Intercom.functions';

/**
 * @module providers/BirdieProvider
 * Provider component that wraps the application with Birdie context.
 *
 * @description This provider initializes Birdie screen recording and makes its functionality
 * available to all child components through the BirdieContext. It requires user data to be
 * available before initialization.
 *
 * @param {tBirdieProviderProps} props - Provider props containing children
 * @returns {JSX.Element} Provider component wrapping children
 *
 * @example
 * // Wrap your app with BirdieProvider
 * <BirdieProvider>
 *   <App />
 * </BirdieProvider>
 */
export default function BirdieProvider(props: tBirdieProviderProps) {
  const { children } = props;
  const { currentUser, currentUserRole } = useUserContext();
  const { subscription } = useSubscriptionContext();
  const { currentWorkspace } = useWorkspaceContext();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const birdieInitialized = useRef<boolean>(false);

  useEffect(() => {
    // Only initialize Birdie once and when user data is available
    if (!birdieInitialized.current && currentUser && subscription?.customerId && VITE_BIRDIE_API_KEY) {
      try {
        initBirdie({
          clientId: VITE_BIRDIE_API_KEY,

          // Required for Birdie log capture
          contact: {
            email: currentUser.email,
          },

          // Dynamic metadata from user context
          metadata: {
            user: {
              id: currentUser.uuid,
              email: currentUser.email,
              name: currentUser.name,
              WID: currentWorkspace?.id,
              plan: subscription.planCode
                ? getPlanName(subscription.planCode)
                : '',
              role: currentUserRole,
              customerId: subscription.customerId,
            },
          },

          // Optional hook once Birdie is ready
          onReady(birdie) {
            setIsInitialized(true);
            birdie.on('start', (data) => {
              LLog.info(
                {
                  component: LOG_COMPONENTS.UI,
                  method: 'BirdieProvider.onReady',
                  operation: LOG_OPERATIONS.DEBUG,
                },
                'Recording started',
                data,
              );
            });

            birdie.on('stop', (data) => {
              LLog.info(
                {
                  component: LOG_COMPONENTS.UI,
                  method: 'BirdieProvider.onReady',
                  operation: LOG_OPERATIONS.DEBUG,
                },
                'Recording stopped',
                data,
              );
            });
          },
        });
        birdieInitialized.current = true;
      } catch (error) {
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'BirdieProvider.useEffect',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Failed to initialize Birdie',
          error as Error,
        );
      }
    }
  }, [currentUser, subscription.customerId]);

  const contextValue = useMemo(() => ({
    isInitialized,
  }), [isInitialized]);

  return (
    <BirdieContext.Provider value={contextValue}>
      {children}
    </BirdieContext.Provider>
  );
}
